import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { studioApi } from '../lib/api';
import { toast } from 'sonner';
import { Users, Plus, Pencil, Trash2, Phone, Mail, MapPin, Upload, Building2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const TIPI = ['Privato', 'Impresa', 'Condominio', 'Ente', 'Altro'];
const FONTI = ['Passaparola', 'Google', 'Social', 'Cliente esistente', 'Impresa', 'Tecnico', 'Altro'];
const TIPO_COLORS = { Privato: 'bg-blue-100 text-blue-800', Impresa: 'bg-emerald-100 text-emerald-800', Condominio: 'bg-amber-100 text-amber-800', Ente: 'bg-purple-100 text-purple-800', Altro: 'bg-gray-100 text-gray-800' };
const empty = {
  nome: '', tipo: 'Privato', telefono: '', email: '', pec: '',
  comune: '', provincia: '', cap: '', indirizzo: '',
  codice_fiscale: '', partita_iva: '',
  denominazione: '', sede_legale: '', capitale_sociale: '', forma_giuridica: '',
  rea: '', ateco: '', data_costituzione: '', legale_rappresentante: '', legale_rappresentante_cf: '',
  fonte: 'Passaparola', note: ''
};

export default function StudioClientiPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      const r = await studioApi.getClienti();
      setItems(r.data);
    } catch { toast.error('Errore caricamento'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm(empty); setEditingId(null); setOpen(true); };
  const openEdit = (c) => {
    setForm({ ...empty, ...c });
    setEditingId(c.id);
    setOpen(true);
  };
  const save = async () => {
    if (!form.nome) { toast.error('Nome obbligatorio'); return; }
    try {
      if (editingId) { await studioApi.updateCliente(editingId, form); toast.success('Aggiornato'); }
      else { await studioApi.createCliente(form); toast.success('Creato'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Eliminare "${c.nome}"?`)) return;
    try { await studioApi.deleteCliente(c.id); toast.success('Eliminato'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };

  const filtered = items.filter(c =>
    !search || (c.nome || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.comune || '').toLowerCase().includes(search.toLowerCase()) ||
    (c.email || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-4 p-4 lg:p-6" data-testid="studio-clienti-page">
      <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div>
          <div className="flex items-center gap-2"><Users className="w-6 h-6 text-indigo-600" /><h1 className="text-2xl font-bold">Clienti</h1><Badge>{items.length}</Badge></div>
          <p className="text-sm text-gray-600 mt-1">Anagrafica clienti dello studio tecnico</p>
        </div>
        <div className="flex gap-2 items-center">
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca..." className="w-56" data-testid="search-clienti" />
          <Button onClick={openCreate} data-testid="new-cliente-btn"><Plus className="w-4 h-4 mr-1" /> Nuovo cliente</Button>
        </div>
      </motion.div>

      {loading ? <p className="text-gray-500 italic">Caricamento...</p> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.map((c) => (
            <motion.div key={c.id} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="bg-white border rounded-lg p-3 shadow-sm" data-testid={`cliente-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold">{c.nome}</h3>
                    <Badge className={`text-xs ${TIPO_COLORS[c.tipo] || TIPO_COLORS.Altro}`}>{c.tipo}</Badge>
                  </div>
                  <div className="text-xs text-gray-600 mt-1.5 space-y-0.5">
                    {c.telefono && <div className="flex items-center gap-1"><Phone className="w-3 h-3" /> {c.telefono}</div>}
                    {c.email && <div className="flex items-center gap-1"><Mail className="w-3 h-3" /> {c.email}</div>}
                    {c.comune && <div className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {c.comune}</div>}
                    {c.fonte && <div className="text-gray-500">Fonte: {c.fonte}</div>}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`edit-cliente-${c.id}`}><Pencil className="w-3.5 h-3.5" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(c)} data-testid={`delete-cliente-${c.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                </div>
              </div>
            </motion.div>
          ))}
          {filtered.length === 0 && <p className="col-span-full text-center text-gray-500 italic py-10">Nessun cliente.</p>}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editingId ? 'Modifica cliente' : 'Nuovo cliente'}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label>Nome / Ragione sociale *</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="cliente-nome" /></div>
            <div><Label>Tipo</Label>
              <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{TIPI.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Fonte</Label>
              <Select value={form.fonte} onValueChange={(v) => setForm({ ...form, fonte: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{FONTI.map(f => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Telefono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
            <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><Label>PEC</Label><Input value={form.pec} onChange={(e) => setForm({ ...form, pec: e.target.value })} /></div>
            <div><Label>Indirizzo</Label><Input value={form.indirizzo} onChange={(e) => setForm({ ...form, indirizzo: e.target.value })} /></div>
            <div><Label>Comune</Label><Input value={form.comune} onChange={(e) => setForm({ ...form, comune: e.target.value })} /></div>
            <div><Label>Provincia</Label><Input value={form.provincia} onChange={(e) => setForm({ ...form, provincia: e.target.value })} maxLength={2} /></div>
            <div><Label>CAP</Label><Input value={form.cap} onChange={(e) => setForm({ ...form, cap: e.target.value })} /></div>
            <div><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} /></div>
            <div><Label>Partita IVA</Label><Input value={form.partita_iva} onChange={(e) => setForm({ ...form, partita_iva: e.target.value })} /></div>

            {/* v38f: Sezione Dati Azienda + Import Camerale */}
            {(form.tipo === 'Impresa' || form.partita_iva) && (
              <div className="md:col-span-2 border-l-4 border-emerald-500 bg-emerald-50/40 rounded p-3 mt-2">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-emerald-700" />
                    <Label className="font-semibold text-sm text-emerald-900">Dati azienda (Camera di Commercio)</Label>
                  </div>
                  {editingId && (
                    <label className="cursor-pointer">
                      <input type="file" accept="application/pdf" className="hidden"
                        onChange={async (e) => {
                          const f = e.target.files?.[0]; if (!f) return;
                          try {
                            const r = await studioApi.importCameraleCliente(editingId, f);
                            const fields = r.data?.imported_fields || [];
                            toast.success(`Importati ${fields.length} campi dalla visura`);
                            // Ricarica i dati nel form
                            const updated = (await studioApi.getClienti()).data.find(c => c.id === editingId);
                            if (updated) setForm({ ...empty, ...updated });
                            load();
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Errore import camerale');
                          }
                        }}
                      />
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700">
                        <Upload className="w-3.5 h-3.5" /> Importa Visura
                      </span>
                    </label>
                  )}
                </div>
                {!editingId && (
                  <p className="text-xs text-emerald-700 italic mb-2">Salva prima il cliente, poi potrai importare la Visura Camerale.</p>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  <div><Label className="text-xs">Denominazione</Label><Input value={form.denominazione} onChange={(e) => setForm({ ...form, denominazione: e.target.value })} className="text-sm" /></div>
                  <div><Label className="text-xs">Forma giuridica</Label><Input value={form.forma_giuridica} onChange={(e) => setForm({ ...form, forma_giuridica: e.target.value })} className="text-sm" /></div>
                  <div className="md:col-span-2"><Label className="text-xs">Sede legale</Label><Input value={form.sede_legale} onChange={(e) => setForm({ ...form, sede_legale: e.target.value })} className="text-sm" /></div>
                  <div><Label className="text-xs">Capitale sociale</Label><Input value={form.capitale_sociale} onChange={(e) => setForm({ ...form, capitale_sociale: e.target.value })} className="text-sm" /></div>
                  <div><Label className="text-xs">N° REA</Label><Input value={form.rea} onChange={(e) => setForm({ ...form, rea: e.target.value })} className="text-sm" /></div>
                  <div><Label className="text-xs">Codice ATECO</Label><Input value={form.ateco} onChange={(e) => setForm({ ...form, ateco: e.target.value })} className="text-sm" /></div>
                  <div><Label className="text-xs">Data costituzione</Label><Input value={form.data_costituzione} onChange={(e) => setForm({ ...form, data_costituzione: e.target.value })} className="text-sm" /></div>
                  <div><Label className="text-xs">Legale rappresentante</Label><Input value={form.legale_rappresentante} onChange={(e) => setForm({ ...form, legale_rappresentante: e.target.value })} className="text-sm" /></div>
                  <div><Label className="text-xs">CF legale rappresentante</Label><Input value={form.legale_rappresentante_cf} onChange={(e) => setForm({ ...form, legale_rappresentante_cf: e.target.value })} className="text-sm" /></div>
                </div>
              </div>
            )}
            <div className="md:col-span-2"><Label>Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-cliente-btn">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
